import { formatDate } from '@msaf/core-common'
import {
  Button,
  Card,
  ColumnHeader,
  ConfirmTransition,
  HeadingRow,
  InlineNotification,
  RecordFooter,
  Table,
  TableAction,
  TableSortByProps,
} from '@msaf/core-react'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ContentContainer } from '../../../../../components/content-container'
import { ERROR_MESSAGES } from '../../../../../components/errors/messages'
import FormEditButtons from '../../../../../components/form-edit-buttons'
import { LoadingPage } from '../../../../../components/loading-page'
import { DEFAULT_TABLE_RESULTS_PER_PAGE, DISPLAY_DATE_FORMAT } from '../../../../../constants'
import { useGetFarmData, useSaveFarmData } from '../../../../../hooks/useApi'
import { useBackLink } from '../../../../../hooks/useBacklink'
import useEditForm from '../../../../../hooks/useEditForm'
import { usePageHeading } from '../../../../../hooks/usePageHeading'
import useToastMessage from '../../../../../hooks/useToastMessage'
import { useUserPermission } from '../../../../../services/permissions'
import { HttpVerb } from '../../../../../types/api-types'
import { ProofOfPurchase } from '../../../../../types/form-types'
import { UserPermissions } from '../../../../../types/permissions'
import { RouteParams } from '../../../../../types/route'

export const FarmFertiliserPurchase = () => {
  const { id } = useParams<RouteParams>()
  usePageHeading('Farm fertiliser purchases')
  useBackLink({ label: 'Dashboard', path: '/dashboard' })
  const [canViewFarmOperations, isLoadingPermissions] = useUserPermission(UserPermissions.MANAGE_FARM)
  const [isDirty, setIsDirty] = useState<boolean>(false)
  const url = `farm-operation/${id}/proof-of-purchase`
  const stateQueryResult = useGetFarmData(url)
  const saveMutationResult = useSaveFarmData(url, HttpVerb.POST)
  const navigate = useNavigate()
  const [fields, setFields] = useState<Record<string, string | number | boolean>[] | null>([])

  const {
    fields: proofOfPurchases,
    isLoading,
    isError,
  } = useEditForm<Array<ProofOfPurchase>>({
    stateQueryResult,
    saveMutationResult,
    setIsDirty,
  })

  useEffect(() => {
    if (proofOfPurchases.length && proofOfPurchases.every((item) => item && Object.keys(item).length)) {
      const withFormattedDates = proofOfPurchases.map((item) => {
        return { ...item, dateTimePurchased: formatDate(item?.dateTimePurchased, DISPLAY_DATE_FORMAT) ?? '' }
      })
      setFields(withFormattedDates as unknown as Record<string, string | number | boolean>[] | null)
    }
  }, [proofOfPurchases?.length, proofOfPurchases])

  useToastMessage({
    isError: isError,
    errorMessage: ERROR_MESSAGES.FETCH_FAILED,
  })

  const [currentPage, setCurrentpage] = useState(1)
  const defaultSortBy: TableSortByProps = { orderColumn: 'dateTimePurchased', orderDirection: 'desc' }
  const [sortBy, setSortBy] = useState<TableSortByProps>()

  const setPage = useCallback(
    (page: number) => {
      if (fields && page >= 1 && page <= fields?.length) {
        setCurrentpage(page)
      }
    },
    [fields],
  )

  const setSortColumn = (orderColumn: string, orderDirection: 'desc' | 'asc') => {
    setSortBy({ orderColumn, orderDirection })
  }

  const rowClickAction: TableAction = {
    label: '',
    type: 'transitionAction',
    args: [{ constant: `farm/edit/${id}/proof-of-purchase` }, { elementKey: 'proofOfPurchaseId' }],
  }

  const actions: Array<TableAction> = [
    {
      label: 'Edit',
      type: 'transitionAction',
      args: [{ constant: `farm/edit/${id}/proof-of-purchase` }, { elementKey: 'proofOfPurchaseId' }],
    },
  ]

  const columnHeaders: Array<ColumnHeader> = [
    {
      elementKey: 'proofOfPurchaseId',
      columnHeading: 'Proof of purchase id',
      viewColumn: 'proofOfPurchaseId',
      type: 'hidden',
    },
    {
      elementKey: 'dateTimePurchased',
      columnHeading: 'Date Purchased',
      viewColumn: 'dateTimePurchased',
      sortable: true,
      type: 'text',
    },
    {
      elementKey: 'supplierName',
      columnHeading: 'Supplier name',
      viewColumn: 'supplierName',
      sortable: true,
      type: 'text',
    },
    {
      elementKey: 'actions',
      columnHeading: 'Actions',
      viewColumn: 'actions',
      type: 'actions-no-data',
      actions,
    },
  ]

  if (isLoading || isLoadingPermissions) return <LoadingPage />
  return (
    <>
      <ConfirmTransition
        when={isDirty}
        contentLabel='Unsaved changes confirmation dialog'
        actionConfig={[
          {
            key: 'continue',
            type: 'cancel',
            buttonStyle: 'primary',
            label: 'Continue editing',
          },
          {
            key: 'discard',
            type: 'confirm',
            buttonStyle: 'text-action',
            label: 'Discard changes',
          },
        ]}
      />
      <ContentContainer>
        {canViewFarmOperations && (
          <HeadingRow headingText='Fertiliser purchases' headingLevel={2} isSkeleton={isLoading}>
            <Button
              iconAlignment='left'
              icon='add'
              label='Add new'
              onClick={() => navigate(`/farm/edit/${id}/proof-of-purchase/new`)}
            />
          </HeadingRow>
        )}

        {fields?.length ? (
          <>
            <Card isFullWidth>
              {/* TODO confirm table sorting operation */}
              <Table
                setSortColumn={setSortColumn}
                setPage={setPage}
                defaultSortBy={sortBy ?? defaultSortBy}
                totalPages={Math.ceil(fields.length / DEFAULT_TABLE_RESULTS_PER_PAGE)}
                currentPage={currentPage}
                pageSize={DEFAULT_TABLE_RESULTS_PER_PAGE}
                totalResults={fields.length}
                isSkeleton={isLoading}
                columnHeaders={columnHeaders}
                tableData={fields}
                onRowClick={rowClickAction}
                noResultsMessage='Please add a fertiliser purchase for your farm to continue'
              />
            </Card>
            <RecordFooter>
              <FormEditButtons
                actions={[
                  {
                    label: 'Save and continue',
                    onClick: () => navigate(`/farm/edit/${id}/land-holding`),
                  },
                  {
                    label: 'Cancel',
                    onClick: () => navigate('/dashboard'),
                    buttonStyle: 'text-action',
                  },
                ]}
                isLoading={isLoading}
              />
            </RecordFooter>
          </>
        ) : (
          <InlineNotification isDismissable={false}>
            Please add a fertiliser purchase for your farm to continue
          </InlineNotification>
        )}
      </ContentContainer>
    </>
  )
}
